// src/lib/metrics/rolling.ts
/**
 * Rolling window calculations - generic functions for any windowed metric
 * 
 * All rolling functions preserve input array length for date alignment.
 * NaN values in input are skipped when building windows, but output
 * maintains alignment with input indices.
 */

import type { Series } from './types';
import { TRADING_DAYS } from './types';
import { filterValid, mean, stdDev, downsideDeviation, upsideDeviation } from './primitives';

// ============================================================================
// INTERNAL HELPERS
// ============================================================================

/**
 * Get valid values from a window, handling NaN/null
 */
function getWindowValues(values: Series, endIdx: number, window: number): number[] {
  const start = Math.max(0, endIdx - window + 1);
  const windowSlice = values.slice(start, endIdx + 1);
  return filterValid(windowSlice);
}

// ============================================================================
// GENERIC ROLLING FRAMEWORK
// ============================================================================

/**
 * Apply any calculation function over a rolling window
 * Preserves input array length for alignment with dates.
 * 
 * @param values - Input series (may contain null/NaN)
 * @param window - Window size
 * @param fn - Function to apply to each window's valid values
 * @param minPoints - Minimum valid points for calculation
 */
export function rolling<T>(
  values: Series,
  window: number,
  fn: (windowValues: number[]) => T,
  minPoints?: number
): (T | null)[] {
  const minPts = minPoints ?? Math.max(2, Math.floor(window * 0.5));
  const results: (T | null)[] = [];
  
  for (let i = 0; i < values.length; i++) {
    const windowValues = getWindowValues(values, i, window);
    
    if (windowValues.length >= minPts) {
      results.push(fn(windowValues));
    } else {
      results.push(null);
    }
  }
  
  return results;
}

// ============================================================================
// ROLLING VOLATILITY
// ============================================================================

/**
 * Calculate rolling volatility (annualized standard deviation)
 * Output length matches input length for date alignment.
 * 
 * @param returns - Array of period returns
 * @param window - Rolling window size
 * @param minPoints - Minimum points for valid calculation
 * @param tradingDays - Trading days for annualization
 * @returns Array of annualized volatility values (as decimals), null for insufficient data
 */
export function rollingVolatility(
  returns: Series,
  window: number,
  minPoints?: number,
  tradingDays: number = TRADING_DAYS.CRYPTO
): (number | null)[] {
  const minPts = minPoints ?? Math.max(20, Math.floor(window * 0.6));
  const result: (number | null)[] = [];
  
  for (let i = 0; i < returns.length; i++) {
    const windowReturns = getWindowValues(returns, i, window);
    
    if (windowReturns.length >= minPts) {
      const s = stdDev(windowReturns);
      if (s !== null) {
        result.push(s * Math.sqrt(tradingDays));
      } else {
        result.push(null);
      }
    } else {
      result.push(null);
    }
  }
  
  return result;
}

/**
 * Rolling volatility as percentage (e.g., 0.25 → 25%)
 */
export function rollingVolatilityPercent(
  returns: Series,
  window: number,
  minPoints?: number,
  tradingDays: number = TRADING_DAYS.CRYPTO
): (number | null)[] {
  return rollingVolatility(returns, window, minPoints, tradingDays)
    .map(v => v !== null ? v * 100 : null);
}

// ============================================================================
// ROLLING SHARPE & SORTINO
// ============================================================================

/**
 * Calculate rolling Sharpe ratio
 * Output length matches input length for date alignment.
 */
export function rollingSharpe(
  returns: Series,
  window: number,
  minPoints?: number,
  tradingDays: number = TRADING_DAYS.CRYPTO
): (number | null)[] {
  const minPts = minPoints ?? Math.max(20, Math.floor(window * 0.6));
  const result: (number | null)[] = [];
  
  for (let i = 0; i < returns.length; i++) {
    const windowReturns = getWindowValues(returns, i, window);
    
    if (windowReturns.length >= minPts) {
      const m = mean(windowReturns);
      const s = stdDev(windowReturns);
      
      if (m !== null && s !== null && s > 0) {
        result.push((m / s) * Math.sqrt(tradingDays));
      } else {
        result.push(null);
      }
    } else {
      result.push(null);
    }
  }
  
  return result;
}

/**
 * Calculate rolling Sortino ratio
 * Output length matches input length for date alignment.
 */
export function rollingSortino(
  returns: Series,
  window: number,
  minPoints?: number,
  tradingDays: number = TRADING_DAYS.CRYPTO
): (number | null)[] {
  const minPts = minPoints ?? Math.max(20, Math.floor(window * 0.6));
  const result: (number | null)[] = [];
  
  for (let i = 0; i < returns.length; i++) {
    const windowReturns = getWindowValues(returns, i, window);
    
    if (windowReturns.length >= minPts) {
      const m = mean(windowReturns);
      const dd = downsideDeviation(windowReturns);
      
      if (m !== null && dd !== null && dd > 0) {
        result.push((m / dd) * Math.sqrt(tradingDays));
      } else {
        result.push(null);
      }
    } else {
      result.push(null);
    }
  }
  
  return result;
}

// ============================================================================
// ROLLING UPSIDE/DOWNSIDE DEVIATION
// ============================================================================

/**
 * Calculate rolling upside deviation (annualized)
 * Output length matches input length for date alignment.
 * 
 * @param returns - Array of returns
 * @param window - Rolling window size
 * @param threshold - Return threshold (default 0)
 * @param minPoints - Minimum points for valid calculation
 * @param tradingDays - Trading days for annualization
 */
export function rollingUpsideDeviation(
  returns: Series,
  window: number,
  threshold: number = 0,
  minPoints?: number,
  tradingDays: number = TRADING_DAYS.CRYPTO
): (number | null)[] {
  const minPts = minPoints ?? Math.max(20, Math.floor(window * 0.6));
  const result: (number | null)[] = [];
  
  for (let i = 0; i < returns.length; i++) {
    const windowReturns = getWindowValues(returns, i, window);
    
    if (windowReturns.length >= minPts) {
      const ud = upsideDeviation(windowReturns, threshold);
      if (ud !== null) {
        result.push(ud * Math.sqrt(tradingDays));
      } else {
        result.push(null);
      }
    } else {
      result.push(null);
    }
  }
  
  return result;
}

/**
 * Calculate rolling downside deviation (annualized)
 * Output length matches input length for date alignment.
 * 
 * @param returns - Array of returns
 * @param window - Rolling window size
 * @param threshold - Return threshold (default 0)
 * @param minPoints - Minimum points for valid calculation
 * @param tradingDays - Trading days for annualization
 */
export function rollingDownsideDeviation(
  returns: Series,
  window: number,
  threshold: number = 0,
  minPoints?: number,
  tradingDays: number = TRADING_DAYS.CRYPTO
): (number | null)[] {
  const minPts = minPoints ?? Math.max(20, Math.floor(window * 0.6));
  const result: (number | null)[] = [];
  
  for (let i = 0; i < returns.length; i++) {
    const windowReturns = getWindowValues(returns, i, window);
    
    if (windowReturns.length >= minPts) {
      const dd = downsideDeviation(windowReturns, threshold);
      if (dd !== null) {
        result.push(dd * Math.sqrt(tradingDays));
      } else {
        result.push(null);
      }
    } else {
      result.push(null);
    }
  }
  
  return result;
}

// ============================================================================
// ROLLING DRAWDOWN
// ============================================================================

/**
 * Calculate rolling drawdown from prices
 * Output length matches input length for date alignment.
 */
export function rollingDrawdown(prices: Series): (number | null)[] {
  const result: (number | null)[] = [];
  let peak = -Infinity;
  
  for (const price of prices) {
    if (price === null || price === undefined || !Number.isFinite(price)) {
      result.push(null);
      continue;
    }
    if (price > peak) peak = price;
    const dd = peak > 0 ? (price / peak) - 1 : 0;
    result.push(dd);
  }
  
  return result;
}

/**
 * Calculate rolling max drawdown over a window
 * Output length matches input length for date alignment.
 */
export function rollingMaxDrawdown(
  prices: Series,
  window: number
): (number | null)[] {
  const dd = rollingDrawdown(prices);
  const result: (number | null)[] = [];
  
  for (let i = 0; i < dd.length; i++) {
    const start = Math.max(0, i - window + 1);
    const windowDD = dd.slice(start, i + 1).filter((v): v is number => v !== null);
    if (windowDD.length === 0) {
      result.push(null);
    } else {
      result.push(Math.min(...windowDD));
    }
  }
  
  return result;
}

// ============================================================================
// ROLLING MEAN (Moving Average)
// ============================================================================

/**
 * Calculate simple moving average (SMA)
 * Output length matches input length for date alignment.
 */
export function rollingMean(values: Series, window: number): (number | null)[] {
  const result: (number | null)[] = [];
  
  for (let i = 0; i < values.length; i++) {
    const windowValues = getWindowValues(values, i, window);
    const m = mean(windowValues);
    result.push(m);
  }
  
  return result;
}

/**
 * Calculate exponential moving average (EMA)
 * Output length matches input length for date alignment.
 */
export function ema(values: Series, span: number): (number | null)[] {
  if (values.length === 0) return [];
  
  const alpha = 2 / (span + 1);
  const result: (number | null)[] = [];
  let prev: number | null = null;
  
  for (const v of values) {
    if (v === null || v === undefined || !Number.isFinite(v)) {
      result.push(prev); // Carry forward last valid EMA
      continue;
    }
    if (prev === null) {
      prev = v;
    } else {
      prev = alpha * v + (1 - alpha) * prev;
    }
    result.push(prev);
  }
  
  return result;
}

/**
 * Calculate weighted moving average (WMA)
 * Output length matches input length for date alignment.
 */
export function wma(values: Series, window: number): (number | null)[] {
  const result: (number | null)[] = [];
  
  for (let i = 0; i < values.length; i++) {
    const windowValues = getWindowValues(values, i, window);
    
    if (windowValues.length < window) {
      result.push(null);
      continue;
    }
    
    let sum = 0;
    let weightSum = 0;
    
    for (let j = 0; j < windowValues.length; j++) {
      const weight = j + 1;
      sum += windowValues[j]! * weight;
      weightSum += weight;
    }
    
    result.push(sum / weightSum);
  }
  
  return result;
}

// ============================================================================
// ROLLING STATISTICS
// ============================================================================

/**
 * Calculate rolling standard deviation
 * Output length matches input length for date alignment.
 */
export function rollingStdDev(values: Series, window: number): (number | null)[] {
  const result: (number | null)[] = [];
  
  for (let i = 0; i < values.length; i++) {
    const windowValues = getWindowValues(values, i, window);
    
    if (windowValues.length >= 2) {
      const s = stdDev(windowValues);
      result.push(s);
    } else {
      result.push(null);
    }
  }
  
  return result;
}

/**
 * Calculate rolling z-score (current value vs window mean/std)
 * Output length matches input length for date alignment.
 */
export function rollingZScore(values: Series, window: number): (number | null)[] {
  const result: (number | null)[] = [];
  
  for (let i = 0; i < values.length; i++) {
    const current = values[i];
    if (current === null || current === undefined || !Number.isFinite(current)) {
      result.push(null);
      continue;
    }
    
    const windowValues = getWindowValues(values, i, window);
    
    if (windowValues.length >= 2) {
      const m = mean(windowValues);
      const s = stdDev(windowValues);
      
      if (m !== null && s !== null && s > 0) {
        result.push((current - m) / s);
      } else {
        result.push(null);
      }
    } else {
      result.push(null);
    }
  }
  
  return result;
}

/**
 * Calculate Bollinger Bands
 * Output length matches input length for date alignment.
 */
export function bollingerBands(
  values: Series,
  window: number,
  numStdDev: number = 2
): { upper: (number | null)[]; middle: (number | null)[]; lower: (number | null)[] } {
  const middle = rollingMean(values, window);
  const std = rollingStdDev(values, window);
  
  const upper = middle.map((m, i) => {
    const s = std[i] ?? null;
    return m !== null && s !== null ? m + numStdDev * s : null;
  });
  
  const lower = middle.map((m, i) => {
    const s = std[i] ?? null;
    return m !== null && s !== null ? m - numStdDev * s : null;
  });
  
  return { upper, middle, lower };
}

// ============================================================================
// ROLLING VAR & ES
// ============================================================================

/**
 * Calculate rolling Value at Risk (VaR)
 * Output length matches input length for date alignment.
 * 
 * @param returns - Array of returns
 * @param window - Rolling window size
 * @param confidence - Confidence level (default 0.95)
 * @param minPoints - Minimum points for valid calculation
 * @returns Array of VaR values (as decimals, negative numbers)
 */
export function rollingVaR(
  returns: Series,
  window: number,
  confidence: number = 0.95,
  minPoints?: number
): (number | null)[] {
  const minPts = minPoints ?? Math.max(20, Math.floor(window * 0.6));
  const result: (number | null)[] = [];
  
  for (let i = 0; i < returns.length; i++) {
    const windowReturns = getWindowValues(returns, i, window);
    
    if (windowReturns.length >= minPts) {
      const sorted = [...windowReturns].sort((a, b) => a - b);
      const idx = Math.floor((1 - confidence) * sorted.length);
      const clampedIdx = Math.max(0, Math.min(sorted.length - 1, idx));
      result.push(sorted[clampedIdx] ?? null);
    } else {
      result.push(null);
    }
  }
  
  return result;
}

/**
 * Calculate rolling Expected Shortfall (Conditional VaR)
 * Output length matches input length for date alignment.
 * 
 * @param returns - Array of returns
 * @param window - Rolling window size
 * @param confidence - Confidence level (default 0.95)
 * @param minPoints - Minimum points for valid calculation
 * @returns Array of ES values (as decimals, negative numbers)
 */
export function rollingES(
  returns: Series,
  window: number,
  confidence: number = 0.95,
  minPoints?: number
): (number | null)[] {
  const minPts = minPoints ?? Math.max(20, Math.floor(window * 0.6));
  const result: (number | null)[] = [];
  
  for (let i = 0; i < returns.length; i++) {
    const windowReturns = getWindowValues(returns, i, window);
    
    if (windowReturns.length >= minPts) {
      const sorted = [...windowReturns].sort((a, b) => a - b);
      const cutoffIdx = Math.floor((1 - confidence) * sorted.length);
      
      if (cutoffIdx <= 0) {
        result.push(sorted[0] ?? null);
      } else {
        const tail = sorted.slice(0, cutoffIdx);
        if (tail.length > 0) {
          const avgTailLoss = tail.reduce((a, b) => a + b, 0) / tail.length;
          result.push(avgTailLoss);
        } else {
          result.push(sorted[0] ?? null);
        }
      }
    } else {
      result.push(null);
    }
  }
  
  return result;
}

// ============================================================================
// ROLLING SKEWNESS & KURTOSIS
// ============================================================================

/**
 * Calculate rolling skewness
 * Output length matches input length for date alignment.
 * 
 * @param returns - Array of returns
 * @param window - Rolling window size
 * @param minPoints - Minimum points for valid calculation
 * @returns Array of skewness values
 */
export function rollingSkewness(
  returns: Series,
  window: number,
  minPoints?: number
): (number | null)[] {
  const minPts = minPoints ?? Math.max(20, Math.floor(window * 0.6));
  const result: (number | null)[] = [];
  
  for (let i = 0; i < returns.length; i++) {
    const windowReturns = getWindowValues(returns, i, window);
    
    if (windowReturns.length >= minPts) {
      const n = windowReturns.length;
      const m = windowReturns.reduce((s, v) => s + v, 0) / n;
      const m2 = windowReturns.reduce((s, v) => s + Math.pow(v - m, 2), 0) / n;
      const m3 = windowReturns.reduce((s, v) => s + Math.pow(v - m, 3), 0) / n;
      
      const denom = Math.pow(m2, 1.5);
      if (denom > 0 && Number.isFinite(denom)) {
        // Sample skewness with bias correction
        const skew = (m3 / denom) * Math.sqrt(n * (n - 1)) / (n - 2);
        result.push(Number.isFinite(skew) ? skew : null);
      } else {
        result.push(null);
      }
    } else {
      result.push(null);
    }
  }
  
  return result;
}

/**
 * Calculate rolling excess kurtosis
 * Output length matches input length for date alignment.
 * 
 * @param returns - Array of returns
 * @param window - Rolling window size
 * @param minPoints - Minimum points for valid calculation
 * @returns Array of kurtosis values (excess kurtosis, normal = 0)
 */
export function rollingKurtosis(
  returns: Series,
  window: number,
  minPoints?: number
): (number | null)[] {
  const minPts = minPoints ?? Math.max(20, Math.floor(window * 0.6));
  const result: (number | null)[] = [];
  
  for (let i = 0; i < returns.length; i++) {
    const windowReturns = getWindowValues(returns, i, window);
    
    if (windowReturns.length >= minPts) {
      const n = windowReturns.length;
      const m = windowReturns.reduce((s, v) => s + v, 0) / n;
      const m2 = windowReturns.reduce((s, v) => s + Math.pow(v - m, 2), 0) / n;
      const m4 = windowReturns.reduce((s, v) => s + Math.pow(v - m, 4), 0) / n;
      
      if (m2 > 0 && Number.isFinite(m2)) {
        // Excess kurtosis (subtract 3 to get Fisher's definition)
        const kurt = (m4 / (m2 * m2)) - 3;
        result.push(Number.isFinite(kurt) ? kurt : null);
      } else {
        result.push(null);
      }
    } else {
      result.push(null);
    }
  }
  
  return result;
}

